import { Core, Decorative, TopShop } from "@faire/design-tokens";
import { ColorTokenKey } from "@faire/web-api--source/indigofair/data/ColorTokenKey";
import { getEventTheme } from "@faire/web/slate/Theme/eventsThemeColors";

const DEFAULT_COLOR_TOKEN = Core.text.default;

export const getSlateColorToken = (
  colorTokenKey: keyof typeof ColorTokenKey | undefined,
  defaultValue: string = DEFAULT_COLOR_TOKEN
): string => {
  if (colorTokenKey === undefined) {
    return defaultValue;
  }
  return colorTokenToSlateTokenMap[colorTokenKey];
};

const colorTokenToSlateTokenMap: Record<ColorTokenKey, string> = {
  [ColorTokenKey.ACTION_BORDER_DEFAULT]: Core.border.default,
  [ColorTokenKey.ACTION_BORDER_DISABLED]: Core.border.disabled,
  [ColorTokenKey.ACTION_BORDER_ENABLED]: Core.border.enabled,
  [ColorTokenKey.ACTION_BORDER_HOVER]: Core.border.hover,
  [ColorTokenKey.ACTION_BORDER_MUTED]: Core.border.muted,
  [ColorTokenKey.ACTION_BORDER_SUBDUED]: Core.border.subdued,
  [ColorTokenKey.ACTION_ICON_DEFAULT]: Core.icon.default,
  [ColorTokenKey.ACTION_ICON_DISABLED]: Core.icon.disabled,
  [ColorTokenKey.ACTION_ICON_ENABLED]: Core.icon.enabled,
  [ColorTokenKey.ACTION_ICON_HOVER]: Core.icon.hover,
  [ColorTokenKey.ACTION_ICON_MUTED]: Core.icon.inverse,
  [ColorTokenKey.ACTION_ICON_SUBDUED]: Core.icon.subdued,
  [ColorTokenKey.ACTION_SURFACE_DEFAULT]: Core.surface.default,
  [ColorTokenKey.ACTION_SURFACE_DISABLED]: Core.surface.disabled,
  [ColorTokenKey.ACTION_SURFACE_ENABLED]: Core.surface.enabled,
  [ColorTokenKey.ACTION_SURFACE_HOVER]: Core.surface.hover,
  [ColorTokenKey.ACTION_SURFACE_MUTED]: Core.surface.inverse,
  [ColorTokenKey.ACTION_SURFACE_SUBDUED]: Core.surface.subdued,
  [ColorTokenKey.ACTION_TEXT_DEFAULT]: Core.text.default,
  [ColorTokenKey.ACTION_TEXT_DISABLED]: Core.text.disabled,
  [ColorTokenKey.ACTION_TEXT_ENABLED]: Core.text.enabled,
  [ColorTokenKey.ACTION_TEXT_HOVER]: Core.text.hover,
  [ColorTokenKey.ACTION_TEXT_MUTED]: Core.text.inverse,
  [ColorTokenKey.ACTION_TEXT_SUBDUED]: Core.text.subdued,
  [ColorTokenKey.CORE_BACKGROUND_PRIMARY]: Core.surface.inverse,
  [ColorTokenKey.CORE_BACKGROUND_SECONDARY]: Core.surface.muted,
  [ColorTokenKey.CORE_BACKGROUND_TERTIARY]: Core.surface.subdued,
  [ColorTokenKey.CORE_BORDER_CRITICAL]: Core.border.critical,
  [ColorTokenKey.CORE_BORDER_PRIMARY]: Core.border.muted,
  [ColorTokenKey.CORE_BORDER_SUCCESS]: Core.border.success,
  [ColorTokenKey.CORE_BORDER_WARNING]: Core.border.warning,
  [ColorTokenKey.CORE_ICON_CRITICAL]: Core.icon.critical,
  [ColorTokenKey.CORE_ICON_PRIMARY]: Core.icon.default,
  [ColorTokenKey.CORE_ICON_SECONDARY]: Core.icon.subdued,
  [ColorTokenKey.CORE_ICON_SUCCESS]: Core.icon.success,
  [ColorTokenKey.CORE_ICON_WARNING]: Core.icon.warning,
  [ColorTokenKey.CORE_SURFACE_CRITICAL]: Core.surface.critical,
  [ColorTokenKey.CORE_SURFACE_SUCCESS]: Core.surface.success,
  [ColorTokenKey.CORE_SURFACE_WARNING]: Core.surface.warning,
  [ColorTokenKey.CORE_TEXT_CRITICAL]: Core.text.critical,
  [ColorTokenKey.CORE_TEXT_PRIMARY]: Core.text.default,
  [ColorTokenKey.CORE_TEXT_PRIMARY_INVERSE]: Core.text.inverse,
  [ColorTokenKey.CORE_TEXT_SECONDARY]: Core.text.subdued,
  [ColorTokenKey.DECORATIVE_ICON_10]: Decorative.icon.neutral,
  [ColorTokenKey.DECORATIVE_ICON_20]: Decorative.icon.red,
  [ColorTokenKey.DECORATIVE_ICON_30]: Decorative.icon.orange,
  [ColorTokenKey.DECORATIVE_ICON_40]: Decorative.icon.yellow,
  [ColorTokenKey.DECORATIVE_ICON_50]: Decorative.icon.green,
  [ColorTokenKey.DECORATIVE_ICON_60]: Decorative.icon.teal,
  [ColorTokenKey.DECORATIVE_ICON_70]: Decorative.icon.blue,
  [ColorTokenKey.DECORATIVE_SHADE_10]: Decorative.shade.neutral,
  [ColorTokenKey.DECORATIVE_SHADE_20]: Decorative.shade.red,
  [ColorTokenKey.DECORATIVE_SHADE_30]: Decorative.shade.orange,
  [ColorTokenKey.DECORATIVE_SHADE_40]: Decorative.shade.yellow,
  [ColorTokenKey.DECORATIVE_SHADE_50]: Decorative.shade.green,
  [ColorTokenKey.DECORATIVE_SHADE_60]: Decorative.shade.teal,
  [ColorTokenKey.DECORATIVE_SHADE_70]: Decorative.shade.blue,
  [ColorTokenKey.DECORATIVE_TINT_10]: Decorative.tint.neutral,
  [ColorTokenKey.DECORATIVE_TINT_20]: Decorative.tint.red,
  [ColorTokenKey.DECORATIVE_TINT_30]: Decorative.tint.orange,
  [ColorTokenKey.DECORATIVE_TINT_40]: Decorative.tint.yellow,
  [ColorTokenKey.DECORATIVE_TINT_50]: Decorative.tint.green,
  [ColorTokenKey.DECORATIVE_TINT_60]: Decorative.tint.teal,
  [ColorTokenKey.DECORATIVE_TINT_70]: Decorative.tint.blue,
  [ColorTokenKey.TOP_SHOP_ICON_GREEN]: TopShop.icon.green,
  [ColorTokenKey.TOP_SHOP_SHADE_GREEN]: TopShop.shade.green,
  [ColorTokenKey.TOP_SHOP_TINT_GREEN]: TopShop.tint.green,
  [ColorTokenKey.CORE_SURFACE_ENABLED]: Core.surface.enabled,
  [ColorTokenKey.CORE_SURFACE_DISABLED]: Core.surface.disabled,
  [ColorTokenKey.CORE_SURFACE_PRIMARY_MARKET]:
    getEventTheme("faireMarket").primaryDark,
  [ColorTokenKey.CORE_BADGE_ICON_MARKET]:
    getEventTheme("faireMarket").badgeIcon,
  [ColorTokenKey.CORE_BACKGROUND_PRIMARY_MARKET]:
    getEventTheme("faireMarket").lightBackground,
  [ColorTokenKey.COLOR_TOKEN_KEY_UNKNOWN]: DEFAULT_COLOR_TOKEN,
};
